import React from 'react';
import { CheckSquareOutlined, LeftOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { Button, Spin } from 'antd';
import { PRETTY_DATE_TIME_FORMAT } from '@owl-frontend/components';
import { Locale } from '../../context/AppProvider';
import type { StoreState } from '../../data/store';
import ArtifactDetail from '../ArtifactsTab/ArtifactDetail';
import ClaimOverviewDetail from '../ClaimsTab/ClaimOverviewDetail';
import { useEscDossierContext } from '../DossierContext';
import { processCitations } from '../extractCitedFiles';
import EscFileViewerPage from '../FileViewerPage/FileViewerPage';
import { EscMarkdown } from '../Markdown/Markdown';
import ReferencesCard from '../ReferencesCard/ReferencesCard';
import TaskDetail from '../TasksTab/TaskDetail';
import { authorDisplay, OWL_AUTHOR_KEY, useCitationHandler } from '../utils';
import WikiPageView from '../WikiTab/WikiPageView';
import styles from './DrawerContent.module.scss';

export type DrawerHistoryItem =
  | { type: 'file'; id: string; page?: number }
  | { type: 'task'; id: string }
  | { type: 'claim'; id: string }
  | { type: 'artifact'; id: string }
  | { type: 'wiki'; path: string }
  | { type?: never };

interface Props {
  fileId: string | null;
  filePage?: number;
  taskId: string | null;
  claimId: string | null;
  artifactId: string | null;
  wikiPath: string | null;
  history: DrawerHistoryItem[];
  onNavigateHistory: (item: DrawerHistoryItem) => void;
}

const ParentBackLink: React.FC<{
  parent: DrawerHistoryItem;
  onNavigate: (item: DrawerHistoryItem) => void;
}> = ({ parent, onNavigate }) => {
  const { messages } = React.useContext(Locale);
  const labelKey =
    parent.type === 'task'
      ? 'esc.drawer.back_to_task'
      : parent.type === 'claim'
      ? 'esc.drawer.back_to_claim'
      : parent.type === 'artifact'
      ? 'esc.drawer.back_to_artifact'
      : parent.type === 'wiki'
      ? 'esc.drawer.back_to_wiki'
      : 'esc.drawer.back_to_file';
  const fallback =
    parent.type === 'task'
      ? 'Back to task'
      : parent.type === 'claim'
      ? 'Back to claim'
      : parent.type === 'artifact'
      ? 'Back to artifact'
      : parent.type === 'wiki'
      ? 'Back to wiki'
      : 'Back';
  return (
    <Button
      type="link"
      size="small"
      icon={<LeftOutlined />}
      onClick={() => onNavigate(parent)}
      className={styles.backLink}
    >
      {messages[labelKey] ?? fallback}
    </Button>
  );
};

const FileTitle: React.FC<{ fileId: string }> = ({ fileId }) => {
  const { messages } = React.useContext(Locale);
  const { dossierId } = useParams<{ dossierId: string }>();
  const file = useSelector(
    (s: StoreState) => s.files.files[dossierId ?? '']?.[fileId] ?? null
  );
  const isActivity = file?.file_type === 'ACTIVITY';
  let title: string | undefined;
  if (isActivity) {
    const rawAuthor = file?.data?.author as string | undefined;
    const author = rawAuthor ? authorDisplay(rawAuthor) : OWL_AUTHOR_KEY;
    title =
      author === OWL_AUTHOR_KEY ? messages['esc.owl_author'] ?? 'Owl' : author;
  } else {
    title = file?.file_name ?? messages['esc.drawer.file_fallback'];
  }
  const when =
    isActivity && file?.created_at
      ? dayjs(file.created_at).format(PRETTY_DATE_TIME_FORMAT)
      : undefined;
  return (
    <>
      <div className={styles.titleRow}>
        <h1 className={styles.title}>{title}</h1>
      </div>
      {when && <div className={styles.titleMeta}>{when}</div>}
    </>
  );
};

const NoteBody: React.FC<{
  description: string;
  header?: React.ReactNode;
  taskId?: string | null;
  taskTitle?: string | null;
}> = ({ description, header, taskId, taskTitle }) => {
  const { onFileClick, onWikiClick, onTaskClick } = useEscDossierContext();
  const onKnowledgeClick = React.useCallback(
    (kbPath: string, anchor?: string) =>
      onWikiClick(`knowledge/${kbPath}${anchor ? `#${anchor}` : ''}`),
    [onWikiClick]
  );
  const handleCitationClick = useCitationHandler(onFileClick, onKnowledgeClick);
  const { renumberedText, references } = React.useMemo(
    () => processCitations(description),
    [description]
  );
  return (
    <div className={styles.noteLayout}>
      <div className={styles.activityBody}>
        {(header || taskId) && (
          <div className={styles.noteHeader}>
            {header}
            {taskId && (
              <button
                type="button"
                className={styles.noteTaskLink}
                onClick={() => onTaskClick(taskId)}
              >
                <CheckSquareOutlined />
                <span>{taskTitle || 'View task'}</span>
              </button>
            )}
          </div>
        )}
        <EscMarkdown
          text={renumberedText || description}
          customCitationHandler={handleCitationClick}
          onPageLink={onWikiClick}
          hidePage
          stateDb
        />
      </div>
      {references.length > 0 && (
        <div className={styles.noteSidebar}>
          <ReferencesCard
            references={references}
            onFileClick={onFileClick}
            onKnowledgeClick={onKnowledgeClick}
          />
        </div>
      )}
    </div>
  );
};

const DrawerContent: React.FC<Props> = ({
  fileId,
  filePage,
  taskId,
  claimId,
  artifactId,
  wikiPath,
  history,
  onNavigateHistory,
}) => {
  const { dossierId } = useParams<{ dossierId: string }>();
  const activeFile = useSelector((s: StoreState) =>
    fileId ? s.files.files[dossierId ?? '']?.[fileId] ?? null : null
  );
  const filesLoaded = useSelector(
    (s: StoreState) => s.files.files[dossierId ?? ''] !== undefined
  );

  const parent = history.length > 1 ? history[history.length - 2] : null;
  // The "Back to …" link the card-based previews render above their body.
  const backLead = parent ? (
    <ParentBackLink parent={parent} onNavigate={onNavigateHistory} />
  ) : undefined;

  if (fileId) {
    if (!activeFile && !filesLoaded) {
      return (
        <div className={styles.loading}>
          <Spin />
        </div>
      );
    }
    if (activeFile?.file_type === 'ACTIVITY') {
      return (
        <div className={styles.container}>
          {parent && (
            <div className={styles.paneHeaderEscalation}>
              <ParentBackLink parent={parent} onNavigate={onNavigateHistory} />
            </div>
          )}
          <div className={styles.fileViewer}>
            <NoteBody
              description={activeFile?.data?.description ?? ''}
              header={<FileTitle fileId={fileId} />}
              taskId={activeFile?.task_id}
              taskTitle={activeFile?.task_title}
            />
          </div>
        </div>
      );
    }
    return (
      <div className={styles.container}>
        <div className={styles.paneHeader}>
          {parent && (
            <ParentBackLink parent={parent} onNavigate={onNavigateHistory} />
          )}
          <FileTitle fileId={fileId} />
        </div>
        <div className={styles.fileViewer}>
          <EscFileViewerPage fileId={fileId} page={filePage} />
        </div>
      </div>
    );
  }

  if (taskId) {
    return (
      <div className={styles.container}>
        {parent && (
          <div className={styles.paneHeaderEscalation}>
            <ParentBackLink parent={parent} onNavigate={onNavigateHistory} />
          </div>
        )}
        <TaskDetail taskId={taskId} />
      </div>
    );
  }

  if (claimId) {
    return (
      <div className={styles.container}>
        {parent && (
          <div className={styles.paneHeaderEscalation}>
            <ParentBackLink parent={parent} onNavigate={onNavigateHistory} />
          </div>
        )}
        <ClaimOverviewDetail claimId={claimId} />
      </div>
    );
  }

  if (artifactId) {
    return (
      <div className={styles.container}>
        {parent && (
          <div className={styles.paneHeaderEscalation}>
            <ParentBackLink parent={parent} onNavigate={onNavigateHistory} />
          </div>
        )}
        <ArtifactDetail artifactId={artifactId} />
      </div>
    );
  }

  if (wikiPath && dossierId) {
    return (
      <WikiPageView dossierId={dossierId} rawPath={wikiPath} lead={backLead} />
    );
  }

  return null;
};

export default DrawerContent;
